import { RepresentationData } from '@ardoq/data-model';
import {
  formatOtherLabel,
  ComponentGraphNode,
  ComponentGraphGroup,
  GroupType,
  WORKSPACE_ICON_REPRESENTATION,
  DEFAULT_MULTI_LABEL_STYLE,
} from '@ardoq/graph';
import { colors } from '@ardoq/design-tokens';
import { BlocksViewNode } from '../types';
import { CreateRelationshipsNodeFromGraphItems } from 'tabview/relationshipDiagrams/types';
import { canvasResolveImage } from 'tabview/canvasRendering/canvasResolvedImages';
import { NODE_DEFAULTS } from '../consts';

const getGroupType = (
  graphNode: ComponentGraphNode | ComponentGraphGroup
): GroupType | null =>
  graphNode.isGroup() ? (graphNode as ComponentGraphGroup).type : null;

const isWorkspaceGroup = (
  graphNode: ComponentGraphNode | ComponentGraphGroup
): graphNode is ComponentGraphGroup =>
  getGroupType(graphNode) === GroupType.WORKSPACE;

const resolveRepresentationData = (
  graphNode: ComponentGraphNode | ComponentGraphGroup
): RepresentationData | null =>
  graphNode.isComponent()
    ? graphNode.getRepresentationData()
    : isWorkspaceGroup(graphNode)
      ? WORKSPACE_ICON_REPRESENTATION
      : null;

const createNode: CreateRelationshipsNodeFromGraphItems<BlocksViewNode> = ({
  graphNode,
  parent,
  contextComponentId,
}) => {
  const representationData = resolveRepresentationData(graphNode);

  if (representationData) {
    canvasResolveImage(representationData);
  }

  const { id, modelId } = graphNode;

  const { mainLabel, subLabel, otherLabels } = graphNode.getItemLabels() ?? {};
  const labels: string[] = [];

  labels.push(mainLabel?.trim() ?? '');
  labels.push(subLabel?.trim() ?? '');

  for (const multilabel of otherLabels ?? []) {
    labels.push(formatOtherLabel(multilabel));
  }

  const multiLabelStyle = otherLabels?.[0]?.[3] ?? DEFAULT_MULTI_LABEL_STYLE;
  const conditionalFormattingState = graphNode.getConditionalFormattingState();

  const node: BlocksViewNode = {
    ...NODE_DEFAULTS,
    conditionalFormattingState,
    id,
    modelId,
    labels,
    multiLabelStyle,
    parent,
    children: null, // to be resolved later
    links: [], // to be resolved later
    representationData,
    isContext: Boolean(contextComponentId && modelId === contextComponentId),
    isComponent: graphNode.isComponent(),
    groupType: getGroupType(graphNode),
    color: graphNode.getCssColorAttributes().fill ?? colors.grey300,
    urlFieldValues: graphNode.getUrlFieldValues() ?? null,
  };
  return node;
};

export { createNode };
